import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from "react-bootstrap";
import { FiCheckCircle, FiRefreshCw, FiSend } from "react-icons/fi";
import api from "../../Utils/api";
import { useFeedback } from "../Generals/Feedback/FeedbackContext";
import EditorRicoAnexos from "../Contato/EditorRicoAnexos";
import MensagemBalao from "../Contato/MensagemBalao";
import "./GodMode.css";

const STATUS = {
  ABERTA: "Aberta",
  AGUARDANDO_USUARIO: "Aguardando usuario",
  AGUARDANDO_SUPORTE: "Aguardando suporte",
  ENCERRADA: "Encerrada",
};

function formatarData(valor) {
  if (!valor) return "-";
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return "-";
  return data.toLocaleString("pt-BR");
}

function MensagensGodMode() {
  const { toast } = useFeedback();
  const [carregando, setCarregando] = useState(false);
  const [conversas, setConversas] = useState([]);
  const [status, setStatus] = useState("");
  const [busca, setBusca] = useState("");
  const [selecionada, setSelecionada] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const editorRef = useRef(null);

  const tituloModal = useMemo(() => {
    if (!selecionada) return "Mensagem";
    return `#${selecionada.id} - ${selecionada.titulo}`;
  }, [selecionada]);

  const carregar = async () => {
    setCarregando(true);
    try {
      const { data } = await api.get("/api/godmode/mensagens", {
        params: { status: status || undefined, busca: busca || undefined, limite: 80 },
      });
      setConversas(data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Nao foi possivel carregar as mensagens.");
    } finally {
      setCarregando(false);
    }
  };

  const abrir = async (conversa) => {
    try {
      const { data } = await api.get(`/api/godmode/mensagens/${conversa.id}`);
      setSelecionada(data.conversa);
      setMensagens(data.mensagens || []);
      if (editorRef.current) editorRef.current.limpar();
    } catch (err) {
      toast.error(err.response?.data?.message || "Nao foi possivel abrir a mensagem.");
    }
  };

  const responder = async () => {
    const conteudo = editorRef.current ? editorRef.current.obterConteudo() : null;
    if (!selecionada || !conteudo || (!conteudo.mensagem_texto.trim() && conteudo.anexos.length === 0)) {
      toast.warning("Informe a resposta.");
      return;
    }
    setSalvando(true);
    try {
      await api.post(`/api/godmode/mensagens/${selecionada.id}/responder`, {
        mensagem_html: conteudo.mensagem_html,
        mensagem_texto: conteudo.mensagem_texto,
        anexos: conteudo.anexos,
      });
      toast.success("Resposta enviada para os alertas do usuario.");
      await abrir(selecionada);
      await carregar();
    } catch (err) {
      toast.error(err.response?.data?.message || "Nao foi possivel responder.");
    } finally {
      setSalvando(false);
    }
  };

  const encerrar = async () => {
    if (!selecionada) return;
    setSalvando(true);
    try {
      await api.post(`/api/godmode/mensagens/${selecionada.id}/encerrar`);
      toast.success("Conversa encerrada.");
      setSelecionada(null);
      await carregar();
    } catch (err) {
      toast.error(err.response?.data?.message || "Nao foi possivel encerrar.");
    } finally {
      setSalvando(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="godmode-clientes-card godmode-mensagens">
      <Card>
        <Card.Header className="d-flex align-items-center justify-content-between gap-2">
          <div>
            <strong>Mensagens</strong>
            <div className="text-muted small">Contato, respostas e conversas com usuarios.</div>
          </div>
          <Button size="sm" variant="outline-primary" onClick={carregar} disabled={carregando}>
            {carregando ? <Spinner animation="border" size="sm" /> : <FiRefreshCw />}
            Atualizar
          </Button>
        </Card.Header>
        <Card.Body>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Form.Control
              size="sm"
              style={{ maxWidth: 360 }}
              placeholder="Buscar por titulo, entidade ou usuario"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") carregar();
              }}
            />
            <Form.Select size="sm" style={{ maxWidth: 220 }} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Todos os status</option>
              {Object.entries(STATUS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Form.Select>
            <Button size="sm" onClick={carregar}>Filtrar</Button>
          </div>

          <Table hover responsive size="sm">
            <thead>
              <tr>
                <th>#</th>
                <th>Assunto</th>
                <th>Titulo</th>
                <th>Entidade</th>
                <th>Usuario</th>
                <th>Status</th>
                <th>Ultima mensagem</th>
              </tr>
            </thead>
            <tbody>
              {conversas.map((conversa) => (
                <tr key={conversa.id} onClick={() => abrir(conversa)} className="godmode-mensagem-row">
                  <td>{conversa.id}</td>
                  <td>{conversa.tipo_label}</td>
                  <td>{conversa.titulo}</td>
                  <td>{conversa.entidade_nome}</td>
                  <td>{conversa.usuario_nome || conversa.usuario_email || "-"}</td>
                  <td>
                    <Badge bg={conversa.status === "ENCERRADA" ? "secondary" : conversa.status === "AGUARDANDO_SUPORTE" ? "warning" : "primary"}>
                      {STATUS[conversa.status] || conversa.status}
                    </Badge>
                  </td>
                  <td>{formatarData(conversa.ultima_mensagem_em)}</td>
                </tr>
              ))}
              {!carregando && conversas.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <Alert variant="light" className="mb-0">Nenhuma mensagem encontrada.</Alert>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={Boolean(selecionada)} onHide={() => setSelecionada(null)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{tituloModal}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selecionada && (
            <>
              <div className="godmode-mensagem-meta">
                <span>{selecionada.tipo_label}</span>
                <span>{STATUS[selecionada.status] || selecionada.status}</span>
                <span>Entidade {selecionada.id_entidade}</span>
                <span>{selecionada.usuario_nome || selecionada.usuario_email || "Usuario nao identificado"}</span>
              </div>
              <div className="godmode-mensagem-thread">
                {mensagens.map((mensagem) => (
                  <MensagemBalao
                    key={mensagem.id}
                    mensagem={mensagem}
                    autor={mensagem.origem === "GODMODE" ? "Duxen" : "Usuario"}
                    titulo={`Mensagem de ${mensagem.origem === "GODMODE" ? "Duxen" : "Usuario"}`}
                    className={`godmode-mensagem-balao ${mensagem.origem === "GODMODE" ? "godmode" : "usuario"}`}
                  />
                ))}
              </div>

              {selecionada.status !== "ENCERRADA" && (
                <Form.Group className="mt-3">
                  <Form.Label>Resposta para o usuario</Form.Label>
                  <EditorRicoAnexos
                    ref={editorRef}
                    placeholder="Escreva a resposta que aparecera no alerta do usuario..."
                    disabled={salvando}
                  />
                </Form.Group>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setSelecionada(null)}>Fechar</Button>
          {selecionada?.status !== "ENCERRADA" && (
            <>
              <Button variant="outline-danger" onClick={encerrar} disabled={salvando}>
                <FiCheckCircle /> Encerrar
              </Button>
              <Button onClick={responder} disabled={salvando}>
                {salvando ? <Spinner animation="border" size="sm" /> : <FiSend />} Responder
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default MensagensGodMode;
